<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  modelValue: number
  min?: number
  max?: number
  step?: number
}>()

const emit = defineEmits<{
  'update:modelValue': [value: number]
}>()

const min = computed(() => props.min ?? 1)
const max = computed(() => props.max ?? 100)
const step = computed(() => props.step ?? 1)

const fillPercent = computed(() => ((props.modelValue - min.value) / (max.value - min.value)) * 100)

function onInput(event: Event) {
  const target = event.target as HTMLInputElement
  emit('update:modelValue', Number(target.value))
}
</script>

<template>
  <div class="kwh-slider">
    <div class="kwh-slider__header">
      <label class="kwh-slider__label" for="kwh-slider-input">Hvor mange kWh skal du lade?</label>
      <span class="kwh-slider__value" aria-hidden="true">
        <strong>{{ modelValue }}</strong> kWh
      </span>
    </div>
    <input
      id="kwh-slider-input"
      class="kwh-slider__input"
      type="range"
      :min="min"
      :max="max"
      :step="step"
      :value="modelValue"
      :style="{ '--fill': `${fillPercent}%` }"
      aria-label="Vælg kWh-behov"
      :aria-valuemin="min"
      :aria-valuemax="max"
      :aria-valuenow="modelValue"
      :aria-valuetext="`${modelValue} kilowattimer`"
      @input="onInput"
    />
    <div class="kwh-slider__scale" aria-hidden="true">
      <span>{{ min }} kWh</span>
      <span>{{ max }} kWh</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.kwh-slider {
  display: flex;
  flex-direction: column;
  gap: space(2);

  &__header {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: baseline;
    gap: space(2) space(3);
  }

  &__label {
    font-size: $font-size-sm;
    font-weight: $font-weight-semibold;
    color: color('ink');
  }

  &__value {
    flex-shrink: 0;
    font-size: $font-size-md;
    color: color('ink-light');
    white-space: nowrap;

    strong {
      font-size: $font-size-xl;
      font-weight: $font-weight-bold;
      color: color('accent-dark');
    }
  }

  &__input {
    appearance: none;
    width: 100%;
    height: 8px;
    background: linear-gradient(
      to right,
      color('accent') 0%,
      color('accent') var(--fill, 50%),
      color('background-light') var(--fill, 50%),
      color('background-light') 100%
    );
    border-radius: $radius-pill;
    outline: none;
    cursor: pointer;

    &::-webkit-slider-thumb {
      appearance: none;
      width: 24px;
      height: 24px;
      background: color('white');
      border: 3px solid color('accent');
      border-radius: 50%;
      cursor: pointer;
      box-shadow: $shadow-sm;
      transition: transform $duration-fast $easing-standard;
    }

    &::-moz-range-thumb {
      width: 24px;
      height: 24px;
      background: color('white');
      border: 3px solid color('accent');
      border-radius: 50%;
      cursor: pointer;
      box-shadow: $shadow-sm;
    }

    &:focus-visible::-webkit-slider-thumb {
      outline: 3px solid color('accent-dark');
      outline-offset: 3px;
    }

    &:focus-visible::-moz-range-thumb {
      outline: 3px solid color('accent-dark');
      outline-offset: 3px;
    }
  }

  &__scale {
    display: flex;
    justify-content: space-between;
    font-size: $font-size-xs;
    color: color('ink-light');
  }
}
</style>
